import { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Award, Plus, Save, Trash2, Loader2, X, Info, Search, Edit3,
} from 'lucide-react';
import { toast } from 'sonner';
import BadgeIcon from '../components/BadgeIcons';

const ICON_OPTIONS = [
  { value: 'steadfast', label: 'الاستمرارية' },
  { value: 'perseverance', label: 'الديمومة' },
  { value: 'resolve', label: 'العزيمة' },
  { value: 'year', label: 'السنة القرائية' },
  { value: 'book_arch', label: 'قوس الكتب' },
  { value: 'library', label: 'المكتبة' },
  { value: 'mihrab', label: 'المحراب' },
  { value: 'quill', label: 'الريشة' },
  { value: 'waves', label: 'الأمواج' },
  { value: 'variety', label: 'التنوّع' },
  { value: 'olive', label: 'غصن الزيتون' },
  { value: 'peak', label: 'القمة' },
];

const CONDITION_OPTIONS = [
  { value: 'streak_days',     label: 'أيام المواصلة (بعد القمة)' },
  { value: 'books_completed', label: 'الكتب المُنجَزة (بعد القمة)' },
  { value: 'total_pages',     label: 'إجمالي الصفحات (بعد القمة)' },
  { value: 'variety',         label: 'تنوّع القراءة (كتب/مقالات/دراسات)' },
  { value: 'castle_peak',     label: 'بلوغ القمة (مرة واحدة)' },
];

const emptyForm = {
  key: '',
  name: '',
  description: '',
  icon: 'book_arch',
  condition_type: 'books_completed',
  target: 10,
  order: 100,
};

const AdminBadgesPage = () => {
  const [loading, setLoading] = useState(true);
  const [badges, setBadges] = useState([]);
  const [search, setSearch] = useState('');
  const [editingKey, setEditingKey] = useState(null); // null = create form, else the badge key
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/badges');
      setBadges(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل تحميل الأوسمة');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim();
    if (!q) return badges;
    return badges.filter(b => (b.name || '').includes(q) || (b.key || '').includes(q));
  }, [badges, search]);

  const startEdit = (b) => {
    setEditingKey(b.key);
    setForm({
      key: b.key,
      name: b.name || '',
      description: b.description || '',
      icon: b.icon || 'book_arch',
      condition_type: b.condition_type || 'books_completed',
      target: Number(b.target) || 1,
      order: Number(b.order) || 100,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => { setEditingKey(null); setForm(emptyForm); };

  const save = async () => {
    if (!form.name.trim() || !form.key.trim()) {
      toast.error('الاسم والمفتاح مطلوبان');
      return;
    }
    setSaving(true);
    try {
      if (editingKey) {
        // Update
        const { data } = await api.put(`/admin/badges/${encodeURIComponent(editingKey)}`, {
          name: form.name,
          description: form.description,
          icon: form.icon,
          condition_type: form.condition_type,
          target: form.target,
          order: form.order,
        });
        toast.success(data?.message || 'تم تحديث الوسام');
      } else {
        // Create
        await api.post('/admin/badges', form);
        toast.success('تم إنشاء الوسام');
      }
      resetForm();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل الحفظ');
    } finally { setSaving(false); }
  };

  const remove = async (b) => {
    if (!window.confirm(`حذف الوسام «${b.name}»؟ سيُحذف من سجل كل من نالوه أيضاً.`)) return;
    try {
      await api.delete(`/admin/badges/${encodeURIComponent(b.key)}`);
      toast.success('تم الحذف');
      if (editingKey === b.key) resetForm();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل الحذف');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-badges-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-bold text-accent flex items-center gap-2">
          <Award className="w-7 h-7 text-amber-500" /> إدارة الأوسمة
        </h1>
        <p className="text-muted-foreground text-sm mt-2">
          الأوسمة تُمنح بعد بلوغ القارئ قمّة القلاع، وتستأنف عدّاداتها من الصفر عند تلك اللحظة. يمكنك إضافة أوسمة جديدة أو تعديل الأشكال والتحديات والأسماء.
        </p>
      </div>

      {/* Form */}
      <Card className="p-5 md:p-6 border-2 border-amber-200 bg-gradient-to-br from-amber-50/40 to-white">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-bold text-accent flex items-center gap-2">
            {editingKey ? <><Edit3 className="w-5 h-5 text-amber-600" /> تعديل «{form.name || editingKey}»</> : <><Plus className="w-5 h-5 text-amber-600" /> وسام جديد</>}
          </h3>
          {editingKey && (
            <Button variant="ghost" size="sm" onClick={resetForm} data-testid="cancel-edit-btn">
              <X className="w-4 h-4 ml-1" /> إلغاء التعديل
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6">
          {/* Live preview */}
          <div className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-white border border-amber-200 min-w-[200px]">
            <p className="text-[11px] text-muted-foreground">معاينة الشكل</p>
            <div className="w-24 h-24 flex items-center justify-center">
              <BadgeIcon name={form.icon} />
            </div>
            <p className="text-sm font-bold text-accent text-center">{form.name || 'اسم الوسام'}</p>
            <p className="text-[11px] text-muted-foreground text-center leading-tight">
              {form.description || 'وصف مختصر'}
            </p>
          </div>

          {/* Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">المفتاح (بالإنجليزية)</label>
              <Input
                value={form.key}
                disabled={!!editingKey}
                onChange={(e) => setForm({ ...form, key: e.target.value.trim() })}
                placeholder="e.g. books_25"
                dir="ltr"
                className="font-mono text-sm"
                data-testid="badge-key-input"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">اسم الوسام</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="وسام حصاد الكتب"
                data-testid="badge-name-input"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-muted-foreground mb-1 block">الوصف</label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="إنجاز 25 كتاباً بعد القمة"
                data-testid="badge-desc-input"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">الشكل / الأيقونة</label>
              <select
                value={form.icon}
                onChange={(e) => setForm({ ...form, icon: e.target.value })}
                className="w-full h-10 rounded-md border border-input bg-transparent px-3 text-sm"
                data-testid="badge-icon-select"
              >
                {ICON_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">نوع التحدّي</label>
              <select
                value={form.condition_type}
                onChange={(e) => setForm({ ...form, condition_type: e.target.value })}
                className="w-full h-10 rounded-md border border-input bg-transparent px-3 text-sm"
                data-testid="badge-condition-select"
              >
                {CONDITION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">الهدف (الرقم المطلوب)</label>
              <Input
                type="number"
                min="1"
                value={form.target}
                onChange={(e) => setForm({ ...form, target: Math.max(1, Number(e.target.value) || 1) })}
                data-testid="badge-target-input"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">الترتيب في العرض</label>
              <Input
                type="number"
                value={form.order}
                onChange={(e) => setForm({ ...form, order: Number(e.target.value) || 0 })}
                data-testid="badge-order-input"
              />
            </div>
            <div className="md:col-span-2 flex flex-wrap items-center gap-2 mt-2">
              <Button
                onClick={save}
                disabled={saving}
                className="bg-amber-600 hover:bg-amber-700 text-white gap-1"
                data-testid="save-badge-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editingKey ? 'حفظ التعديلات' : 'إضافة الوسام'}
              </Button>
              <div className="flex items-start gap-1.5 text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                  المفتاح ثابت بعد الإنشاء ولا يُعدَّل — استخدم رموزاً إنجليزية مثل <code className="font-mono">books_25</code>.
                </span>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Search + list */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute right-3 top-3 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="بحث بالاسم أو المفتاح..." className="pr-9" data-testid="badges-search" />
        </div>
        <p className="text-sm text-muted-foreground">{loading ? 'جارٍ التحميل...' : `${filtered.length} من ${badges.length} وسام`}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((b) => (
          <Card key={b.key} className="p-4 border transition-all hover:shadow-md" data-testid={`badge-card-${b.key}`}>
            <div className="flex items-start gap-3">
              <div className="w-16 h-16 shrink-0 flex items-center justify-center">
                <BadgeIcon name={b.icon} />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-bold text-accent">{b.name}</h4>
                <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{b.description}</p>
                <div className="flex flex-wrap gap-1 mt-2">
                  <span className="text-[10px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">{CONDITION_OPTIONS.find(c => c.value === b.condition_type)?.label || b.condition_type}</span>
                  <span className="text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded-full">الهدف: {b.target}</span>
                  <span className="text-[10px] bg-muted text-muted-foreground px-2 py-0.5 rounded-full font-mono" dir="ltr">{b.key}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <Button size="sm" variant="outline" onClick={() => startEdit(b)} className="gap-1" data-testid={`edit-badge-${b.key}`}>
                <Edit3 className="w-3.5 h-3.5" /> تعديل
              </Button>
              <Button size="sm" variant="ghost" onClick={() => remove(b)} className="gap-1 text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`delete-badge-${b.key}`}>
                <Trash2 className="w-3.5 h-3.5" /> حذف
              </Button>
            </div>
          </Card>
        ))}
        {filtered.length === 0 && !loading && (
          <Card className="p-10 text-center text-muted-foreground col-span-full">لا توجد أوسمة مطابقة</Card>
        )}
      </div>
    </div>
  );
};

export default AdminBadgesPage;
